import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faArrowLeft, faArchive, faTrash, faDownload, faLink, faFileUpload, faSearch } from '@fortawesome/free-solid-svg-icons';
import { apiJson, apiForm, apiDownload, baixarBlob } from '../apiClient';
import { Panel } from '../../crm/components/ui/Panel';
import { Badge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');
const fmtBytes = (b) => {
  if (!b) return '—';
  const kb = b / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const TIPOS_DOCUMENTO = ['CONTRATO', 'PROPOSTA', 'NOTA_FISCAL', 'COMPROVANTE', 'TERMO', 'ORCAMENTO', 'DOCUMENTO_TECNICO', 'ATA', 'ANEXO', 'OUTRO'];
const TIPO_LABEL = {
  CONTRATO: 'Contrato', PROPOSTA: 'Proposta', NOTA_FISCAL: 'Nota Fiscal', COMPROVANTE: 'Comprovante', TERMO: 'Termo',
  ORCAMENTO: 'Orçamento', DOCUMENTO_TECNICO: 'Documento Técnico', ATA: 'Ata', ANEXO: 'Anexo', OUTRO: 'Outro',
};
const ENTIDADE_LABEL = { CLIENTE: 'Cliente', PROJETO: 'Projeto', ATIVO: 'Ativo', TAREFA: 'Tarefa', COMPRA: 'Compra', RECEIVABLE: 'Receita', PAYABLE: 'Despesa', PAYMENT: 'Pagamento' };

// ─── Lista global ────────────────────────────────────────────────────────
function ListaDocumentos({ onAbrirDocumento, showToast, relacaoInicial }) {
  const [documentos, setDocumentos] = useState(null);
  const [busca, setBusca] = useState('');
  const [tipo, setTipo] = useState('');
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [projetoFiltro, setProjetoFiltro] = useState('');
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [showNovo, setShowNovo] = useState(!!relacaoInicial);

  const carregar = useCallback(async () => {
    const params = new URLSearchParams();
    if (busca) params.set('busca', busca);
    if (tipo) params.set('tipo', tipo);
    if (clienteFiltro) params.set('cliente_id', clienteFiltro);
    if (projetoFiltro) params.set('projeto_id', projetoFiltro);
    const r = await apiJson(`/documentos?${params}`);
    if (r.status === 'ok') setDocumentos(r.lista);
  }, [busca, tipo, clienteFiltro, projetoFiltro]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    apiJson('/clientes').then((r) => { if (r.status === 'ok') setClientes(r.lista); });
    apiJson('/projetos').then((r) => { if (r.status === 'ok') setProjetos(r.lista || []); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!clienteFiltro || !projetoFiltro) return;
    const projeto = projetos.find((p) => p.id === projetoFiltro);
    if (projeto && !projeto.clientes?.some((c) => c.id === clienteFiltro)) setProjetoFiltro('');
  }, [clienteFiltro, projetos]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--crm-space-4)', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          <div className="crm-search-input" style={{ minWidth: '200px' }}>
            <FontAwesomeIcon icon={faSearch} />
            <input className="crm-input" placeholder="Buscar por título..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <select className="crm-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Tipo: Todos</option>
            {TIPOS_DOCUMENTO.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
          </select>
          <select className="crm-select" value={clienteFiltro} onChange={(e) => setClienteFiltro(e.target.value)}>
            <option value="">Cliente: Todos</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
          <select className="crm-select" value={projetoFiltro} onChange={(e) => setProjetoFiltro(e.target.value)}>
            <option value="">Projeto: Todos</option>
            {projetos
              .filter((p) => !clienteFiltro || p.clientes?.some((c) => c.id === clienteFiltro))
              .map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          {(clienteFiltro || projetoFiltro) && (
            <Button size="sm" variant="ghost" onClick={() => { setClienteFiltro(''); setProjetoFiltro(''); }}>Limpar</Button>
          )}
        </div>
        <Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Documento</Button>
      </div>

      <Panel flush>
        {documentos === null ? (
          <EmptyState title="Carregando..." />
        ) : documentos.length === 0 ? (
          <EmptyState title="Nenhum documento encontrado" description="Contratos, propostas, notas fiscais e comprovantes aparecem aqui." actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovo(true)}>Documento</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Nome</th><th>Tipo</th><th>Versão atual</th><th>Atualizado</th></tr></thead>
              <tbody>
                {documentos.map((d) => (
                  <tr key={d.id} className="clickable" onClick={() => onAbrirDocumento(d.id)}>
                    <td>
                      <div className="crm-table-primary">{d.titulo}</div>
                      {d.total_versoes > 1 && <div className="crm-table-sub">{d.total_versoes} versões</div>}
                    </td>
                    <td><Badge tone="accent">{TIPO_LABEL[d.tipo] || d.tipo}</Badge></td>
                    <td>{d.versao_atual?.tem_arquivo ? <><FontAwesomeIcon icon={faFileUpload} /> Arquivo</> : <><FontAwesomeIcon icon={faLink} /> Link externo</>}</td>
                    <td>{fmtDateTime(d.atualizado_em)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showNovo && <NovoDocumentoModal onFechar={() => setShowNovo(false)} onSalvo={() => { setShowNovo(false); carregar(); }} showToast={showToast} relacaoInicial={relacaoInicial} clientes={clientes} projetos={projetos} />}
    </div>
  );
}

// ─── Modal: Novo Documento ───────────────────────────────────────────────
function NovoDocumentoModal({ onFechar, onSalvo, showToast, relacaoInicial, clientes, projetos }) {
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('CONTRATO');
  const [descricao, setDescricao] = useState('');
  const [modoLink, setModoLink] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [relacaoTipo, setRelacaoTipo] = useState('');
  const [relacaoId, setRelacaoId] = useState('');
  const [relacoes, setRelacoes] = useState(relacaoInicial ? [relacaoInicial] : []);
  const [salvando, setSalvando] = useState(false);

  const adicionarRelacao = () => {
    if (!relacaoTipo || !relacaoId) return;
    if (relacoes.some((r) => r.entity_type === relacaoTipo && r.entity_id === relacaoId)) return;
    const nome = relacaoTipo === 'CLIENTE' ? clientes.find((c) => c._id === relacaoId)?.nome : projetos.find((p) => p.id === relacaoId)?.nome;
    setRelacoes([...relacoes, { entity_type: relacaoTipo, entity_id: relacaoId, nome }]);
    setRelacaoId('');
  };
  const removerRelacao = (i) => setRelacoes(relacoes.filter((_, idx) => idx !== i));

  const salvar = async () => {
    if (!titulo.trim()) { showToast?.('Informe o título', 'error'); return; }
    if (!modoLink && !arquivo) { showToast?.('Selecione um arquivo ou informe um link', 'error'); return; }
    if (modoLink && !externalUrl.trim()) { showToast?.('Informe o link externo', 'error'); return; }
    setSalvando(true);
    const formData = new FormData();
    formData.append('titulo', titulo);
    formData.append('tipo', tipo);
    formData.append('descricao', descricao);
    if (modoLink) formData.append('external_url', externalUrl);
    else formData.append('arquivo', arquivo);
    formData.append('relacoes', JSON.stringify(relacoes.map(({ entity_type, entity_id }) => ({ entity_type, entity_id }))));
    const result = await apiForm('/documentos', { method: 'POST', body: formData });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.('Documento criado!', 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao criar documento', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Novo Documento" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Criar Documento</Button>}>
      <div className="crm-form-field"><label className="crm-form-label">Título</label><input className="crm-input" value={titulo} onChange={(e) => setTitulo(e.target.value)} /></div>
      <div className="crm-form-field">
        <label className="crm-form-label">Tipo</label>
        <select className="crm-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {TIPOS_DOCUMENTO.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
        </select>
      </div>
      <div className="crm-form-field"><label className="crm-form-label">Descrição (opcional)</label><input className="crm-input" value={descricao} onChange={(e) => setDescricao(e.target.value)} /></div>

      <div className="crm-form-field">
        <label className="crm-form-label">Origem</label>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <Button size="sm" variant={!modoLink ? 'primary' : 'outline'} icon={faFileUpload} onClick={() => setModoLink(false)}>Arquivo</Button>
          <Button size="sm" variant={modoLink ? 'primary' : 'outline'} icon={faLink} onClick={() => setModoLink(true)}>Link externo</Button>
        </div>
      </div>
      {modoLink ? (
        <div className="crm-form-field"><label className="crm-form-label">URL</label><input className="crm-input" placeholder="https://..." value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} /></div>
      ) : (
        <div className="crm-form-field"><label className="crm-form-label">Arquivo</label><input type="file" onChange={(e) => setArquivo(e.target.files[0])} /></div>
      )}

      <div className="crm-form-field">
        <label className="crm-form-label">Relacionar a (opcional)</label>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <select className="crm-select" value={relacaoTipo} onChange={(e) => { setRelacaoTipo(e.target.value); setRelacaoId(''); }} style={{ flex: 1 }}>
            <option value="">Tipo...</option>
            <option value="CLIENTE">Cliente</option>
            <option value="PROJETO">Projeto</option>
          </select>
          <select className="crm-select" value={relacaoId} onChange={(e) => setRelacaoId(e.target.value)} disabled={!relacaoTipo} style={{ flex: 1 }}>
            <option value="">Selecione...</option>
            {(relacaoTipo === 'CLIENTE' ? clientes.map((c) => ({ id: c._id, nome: c.nome })) : projetos.map((p) => ({ id: p.id, nome: p.nome })))
              .map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={adicionarRelacao}>+</Button>
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap', marginTop: 'var(--crm-space-2)' }}>
          {relacoes.map((r, i) => (
            <Badge key={i} tone="accent">{ENTIDADE_LABEL[r.entity_type] || r.entity_type}: {r.nome || r.entity_id.slice(0, 8)} <span style={{ cursor: 'pointer', marginLeft: 4 }} onClick={() => removerRelacao(i)}>×</span></Badge>
          ))}
        </div>
      </div>
    </Sheet>
  );
}

// ─── Detalhe do documento ────────────────────────────────────────────────
function DetalheDocumento({ documentoId, onVoltar, showToast, onNavegar }) {
  const [documento, setDocumento] = useState(null);
  const [showNovaVersao, setShowNovaVersao] = useState(false);

  const carregar = useCallback(async () => { const r = await apiJson(`/documentos/${documentoId}`); if (r.status === 'ok') setDocumento(r.documento); }, [documentoId]);
  useEffect(() => { carregar(); }, [carregar]);

  if (!documento) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const baixarComAuth = async (versionId, nomeArquivo) => {
    const blob = await apiDownload(`/documentos/${documentoId}/versoes/${versionId}/download`);
    if (!blob) { showToast?.('Erro ao baixar arquivo', 'error'); return; }
    baixarBlob(blob, nomeArquivo);
  };

  const arquivar = async () => {
    const result = await apiJson(`/documentos/${documentoId}/arquivar`, { method: 'POST' });
    if (result.status === 'ok') { showToast?.('Documento arquivado!', 'success'); carregar(); }
    else showToast?.(result.mensagem || 'Erro ao arquivar', 'error');
  };

  const excluir = async () => {
    const result = await apiJson(`/documentos/${documentoId}`, { method: 'DELETE' });
    if (result.status === 'ok') { showToast?.('Documento excluído.', 'success'); onVoltar(); }
    else showToast?.(result.mensagem || 'Não foi possível excluir (já relacionado — arquive em vez de excluir)', 'error');
  };

  const abrirRelacao = (r) => {
    if (!onNavegar) return;
    if (r.entity_type === 'CLIENTE') onNavegar('clientes', { clienteId: r.entity_id });
    else if (r.entity_type === 'PROJETO') onNavegar('projetos', { projetoId: r.entity_id });
    else if (r.entity_type === 'ATIVO') onNavegar('patrimonio', { ativoId: r.entity_id });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--crm-space-4)', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)' }}>
          <button className="crm-icon-btn" onClick={onVoltar}><FontAwesomeIcon icon={faArrowLeft} /></button>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>{documento.titulo}</h2>
            <div className="crm-form-hint">{TIPO_LABEL[documento.tipo] || documento.tipo}</div>
          </div>
          {documento.status === 'ARQUIVADO' && <Badge tone="neutral">Arquivado</Badge>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          {documento.status !== 'ARQUIVADO' && <Button size="sm" variant="outline" icon={faPlus} onClick={() => setShowNovaVersao(true)}>Nova versão</Button>}
          {documento.status !== 'ARQUIVADO' && <Button size="sm" variant="outline" icon={faArchive} onClick={arquivar}>Arquivar</Button>}
          <Button size="sm" variant="danger" icon={faTrash} onClick={excluir}>Excluir</Button>
        </div>
      </div>

      {documento.relacoes?.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap', marginBottom: 'var(--crm-space-4)' }}>
          {documento.relacoes.map((r, i) => (
            <span key={i} onClick={() => abrirRelacao(r)} style={{ cursor: onNavegar ? 'pointer' : 'default' }}>
              <Badge tone="accent">{ENTIDADE_LABEL[r.entity_type] || r.entity_type}: {r.nome || r.entity_id.slice(0, 8)}</Badge>
            </span>
          ))}
        </div>
      )}

      <Panel title="Versões" flush>
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead><tr><th>Versão</th><th>Origem</th><th>Tamanho</th><th>Enviado</th><th>Quando</th><th /></tr></thead>
            <tbody>
              {documento.versoes.map((v) => (
                <tr key={v.id}>
                  <td>v{v.version} {v.is_atual && <Badge tone="positive">atual</Badge>}</td>
                  <td>{v.tem_arquivo ? 'Arquivo' : 'Link externo'}</td>
                  <td>{v.tem_arquivo ? fmtBytes(v.tamanho_bytes) : '—'}</td>
                  <td>{v.enviado_por || '—'}</td>
                  <td>{fmtDateTime(v.criado_em)}</td>
                  <td>
                    {v.tem_arquivo ? (
                      <Button size="sm" variant="ghost" icon={faDownload} onClick={() => baixarComAuth(v.id, documento.titulo)} />
                    ) : (
                      <a href={v.external_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" icon={faLink} /></a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {showNovaVersao && <NovaVersaoModal documentoId={documentoId} onFechar={() => setShowNovaVersao(false)} onSalvo={() => { setShowNovaVersao(false); carregar(); }} showToast={showToast} />}
    </div>
  );
}

function NovaVersaoModal({ documentoId, onFechar, onSalvo, showToast }) {
  const [modoLink, setModoLink] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!modoLink && !arquivo) { showToast?.('Selecione um arquivo', 'error'); return; }
    if (modoLink && !externalUrl.trim()) { showToast?.('Informe o link', 'error'); return; }
    setSalvando(true);
    const formData = new FormData();
    if (modoLink) formData.append('external_url', externalUrl);
    else formData.append('arquivo', arquivo);
    const result = await apiForm(`/documentos/${documentoId}/versoes`, { method: 'POST', body: formData });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.('Nova versão enviada!', 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao enviar nova versão', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Nova Versão" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Enviar Nova Versão</Button>}>
      <div style={{ display: 'flex', gap: 'var(--crm-space-2)', marginBottom: 'var(--crm-space-3)' }}>
        <Button size="sm" variant={!modoLink ? 'primary' : 'outline'} icon={faFileUpload} onClick={() => setModoLink(false)}>Arquivo</Button>
        <Button size="sm" variant={modoLink ? 'primary' : 'outline'} icon={faLink} onClick={() => setModoLink(true)}>Link</Button>
      </div>
      {modoLink ? (
        <input className="crm-input" placeholder="https://..." value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} />
      ) : (
        <input type="file" onChange={(e) => setArquivo(e.target.files[0])} />
      )}
    </Sheet>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────
function Documentos({ showToast, documentoIdInicial, relacaoInicial, onNavegar }) {
  const [documentoSelecionado, setDocumentoSelecionado] = useState(documentoIdInicial || null);
  if (documentoSelecionado) {
    return <DetalheDocumento documentoId={documentoSelecionado} onVoltar={() => setDocumentoSelecionado(null)} showToast={showToast} onNavegar={onNavegar} />;
  }
  return <ListaDocumentos onAbrirDocumento={setDocumentoSelecionado} showToast={showToast} relacaoInicial={relacaoInicial} />;
}

export default Documentos;
